import { Box, Typography } from '@mui/material'
import { InfoCircleIcon, TrendDown01Icon, TrendUp01Icon } from '@untitled-theme/icons-react'
import { useState } from 'react'

import { BedarfVerbrauchLabel, BedarfVerbrauchType } from '@/shared/components/BedarfVerbrauchLabel'
import EditISFPDataPopup from '@/shared/components/EditISFPDataPopup'
import { SavingsPotentialInfoPopup } from '@/shared/components/InfoPopup'

interface SavingsPotentialProps {
  avgEnergyCostSavingsWithinFiveYears: {
    inPct?: number
    inCt?: number
  }
  annualFinalEnergyInKwh: {
    current?: number
    retrofit?: number
  }
  showBedarfVerbrauch?: boolean
}

const SavingsPotential = ({
  avgEnergyCostSavingsWithinFiveYears,
  annualFinalEnergyInKwh,
  showBedarfVerbrauch = true,
}: SavingsPotentialProps) => {
  const [isSavingsInfoPopupOpen, setIsSavingsInfoPopupOpen] = useState(false)
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)

  // Calculate savings from retrofitState if provided
  let costValue: string = '– €'
  let energyValue: string = ''

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num)

  let currencySavings = avgEnergyCostSavingsWithinFiveYears.inCt
  if (typeof currencySavings === 'number') {
    currencySavings = currencySavings / 100 // Convert cents to euros

    if (currencySavings < 0) {
      costValue = '< 0 €'
    } else {
      const currencyLower = Math.round(currencySavings * 0.9)
      const currencyUpper = Math.round(currencySavings * 1.1)

      costValue = `${formatNumber(currencyLower)} – ${formatNumber(currencyUpper)} €`
    }
  }

  if (
    typeof annualFinalEnergyInKwh.current === 'number' &&
    typeof annualFinalEnergyInKwh.retrofit === 'number'
  ) {
    const energySavings = annualFinalEnergyInKwh.current - annualFinalEnergyInKwh.retrofit

    const energyLower = Math.round(energySavings * 0.9)
    const energyUpper = Math.round(energySavings * 1.1)

    energyValue = `${formatNumber(energyLower)} – ${formatNumber(energyUpper)} kWh`
  }

  // Use percentage display if retrofitState is not provided
  const percentageDisplay =
    avgEnergyCostSavingsWithinFiveYears.inPct !== undefined
      ? `${avgEnergyCostSavingsWithinFiveYears.inPct.toFixed(2)} %`
      : '– %'

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: 'inset 0 0 0 1px #E1E1E4',
        padding: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
          }}
        >
          Einsparpotenzial
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {!showBedarfVerbrauch && <Box />}
          <InfoCircleIcon
            style={{ width: 18, height: 18, color: '#464A54', cursor: 'pointer' }}
            onClick={() => setIsSavingsInfoPopupOpen(true)}
          />
          {showBedarfVerbrauch && <BedarfVerbrauchLabel type={BedarfVerbrauchType.VERBRAUCH} />}
        </Box>
      </Box>

      {/* Always show cost display */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '4px',
          marginTop: '4px',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          {costValue}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '8px',
          }}
        >
          /Jahr
        </Typography>
      </Box>

      {/* Show energy display only when it has a value */}
      {energyValue && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '4px',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: 400,
              fontSize: '10px',
              color: '#464A54',
            }}
          >
            {energyValue}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: '8px',
              color: '#464A54',
            }}
          >
            /Jahr
          </Typography>
        </Box>
      )}

      {/* Always show percentage display at the bottom */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '4px',
        }}
      >
        {avgEnergyCostSavingsWithinFiveYears.inPct !== undefined &&
        avgEnergyCostSavingsWithinFiveYears.inPct < 0 ? (
          <TrendUp01Icon style={{ width: 12, height: 12, color: '#E53935' }} />
        ) : (
          <TrendDown01Icon style={{ width: 12, height: 12, color: '#5BC06B' }} />
        )}
        <Typography
          sx={{
            color: '#464A54',
            fontWeight: 400,
            fontSize: '9px',
            lineHeight: '14px',
          }}
        >
          Bis zu {percentageDisplay} der Energiekosten pro Jahr sparen
        </Typography>
      </Box>

      <SavingsPotentialInfoPopup
        open={isSavingsInfoPopupOpen}
        onClose={() => setIsSavingsInfoPopupOpen(false)}
        onEdit={() => {
          setIsSavingsInfoPopupOpen(false)
          setIsEditPopupOpen(true)
        }}
      />

      <EditISFPDataPopup open={isEditPopupOpen} onClose={() => setIsEditPopupOpen(false)} />
    </Box>
  )
}

export default SavingsPotential
